import React, { Component } from 'react'
import { connect } from 'react-redux'

// redux
import * as authModule from '../../redux/modules/authentication'
import * as categoriesModule from '../../redux/modules/categories'
import * as followingModule from '../../redux/modules/followings'
import * as peopleModule from '../../redux/modules/people'
import * as storiesModule from '../../redux/modules/stories'

// config
import constants from 'config/constants'
import trackPage from 'config/googleAnalytics'
import l, { c } from 'config/localization'

// utils
import hasWebPSupport from 'utils/hasWebPSupport'
import { getLocalStorageValues } from 'utils/localStorageManager'
import isLoggedIn from 'utils/auth'

// icons
import { UserFollow } from '@styled-icons/remix-line/UserFollow'
import { UserUnfollow } from '@styled-icons/remix-line/UserUnfollow'
import { JournalText } from '@styled-icons/bootstrap/JournalText'
import { StarFill } from '@styled-icons/bootstrap'

// assets
import bpIcon from 'assets/images/bp-logo.png'
import userIcon from 'assets/images/user.svg'
import { ReactComponent as OrgTypeIcon } from 'assets/images/svgs/icon-org-type.svg'
import { ReactComponent as ChallengeIcon } from 'assets/images/cause-type-icons/challenge_icon.svg'

// styles
import {
  ActionLink,
  MissingOrgText,
  MissingOrgTitle,
  OrgAdminActions,
  OrgContainer,
  OrgDescriptionLabel,
  UserSectionWrapperInner,
  NotFoundWrapper,
  OrgDetailLabel,
  OrgIntroInnerWrapper,
  OrgIntroWrapper,
  OrgLogo,
  OrgLogoContainer,
  OrgLogoWrapper,
  OrgName,
  OrgProfileHorizontalSeparator,
  UserSectionWrapper,
  ProfileLink,
  UserCountryIconWrapper,
  UserCountryWrapper,
  UserDetailFallback,
  UserOppsBlock,
  UserProfileStatsIcon,
  UserProfileStatsImage,
  UserProfileStatsImageWrapper,
  UserProfileStatLabel,
  UserProfileStatValue,
  UserProfileStatsRow,
  UserProfileStatsWrapper,
  UserSupportedOrgsBlock,
  ViewMoreLink
} from './styles'

// components
import { Globe, Pencil } from '../../components/Icons/Icons'

import AnimatedLoader from 'components/AnimatedLoader/AnimatedLoader'
import ConditionalDisplay from 'components/ConditionalDisplay/ConditionalDisplay'
import PageWrapper from 'components/PageWrapper/PageWrapper'
import SmallButtonPrimary from 'components/SmallButtonPrimary/SmallButtonPrimary'
import StoryFeed from 'components/StoryFeed/StoryFeed'

const buildHostedOpps = ({ individual }) => {
  const hostedOpps = individual.hostedOpps || []
  const totalHostedOpps = hostedOpps.length

  return { hostedOpps: hostedOpps.slice(0, 4), totalHostedOpps }
}

const buildJoinedOpps = ({ individual }) => {
  const joinedOpps = individual.joinedOpps || []
  const totalJoinedOpps = joinedOpps.length

  return { joinedOpps: joinedOpps.slice(0, 4), totalJoinedOpps }
}

const buildJoinedChallenges = ({ individual }) => {
  const joinedChallenges = individual.joinedChallenges || []
  const totalJoinedChallenges = joinedChallenges.length

  return { joinedChallenges: joinedChallenges.slice(0, 4), totalJoinedChallenges }
}

const buildSupportedOrgs = ({ individual }) => {
  const supportedOrgs = individual.orgsSupported || []
  const stats = individual.stats || {}
  const totalSupportedOrgs = stats.supportedOrgsCount

  return { supportedOrgs: supportedOrgs.slice(0, 4), totalSupportedOrgs }
}

const buildStories = ({ individual }) => {
  const stories = individual.stories || []
  const totalStoryCount = stories.length

  return { stories: stories.slice(0, 4), totalStoryCount }
}

const shouldDisplayPageNotFound = ({ location, individual }) => {
  const path = location.pathname
  const isUsernameFormat = path.match(/^\/@/)
  const isNonUsernameButUserPath = path.match(/^\/users\//) || path.match(/^\/u\//)

  if (isUsernameFormat) return false
  if (isNonUsernameButUserPath && individual) return false

  return true
}

const UserCountrySection = ({ individual }) => {
  if (individual.country) {
    return (
      <UserCountryWrapper>
        <UserCountryIconWrapper>
          <Globe />
        </UserCountryIconWrapper>

        <span>{individual.country}</span>
      </UserCountryWrapper>
    )
  }

  return (
    <UserCountryWrapper>
      <UserCountryIconWrapper>
        <Globe />
      </UserCountryIconWrapper>

      <UserDetailFallback>No location provided</UserDetailFallback>
    </UserCountryWrapper>
  )
}

class UserProfilePage extends Component {
  constructor(props) {
    super(props)

    this.state = {
      doesSupportWebP: false,
      updatedStories: []
    }

    this.followEntity = this.followEntity.bind(this)
    this.unfollowEntity = this.unfollowEntity.bind(this)
  }

  componentDidMount() {
    trackPage(window.location.pathname)

    requestAnimationFrame(() => {
      window.scrollTo(0, 0)
    })

    const { userId, username } = this.props
    this.props.fetchCategories()
    this.props.fetchPublicUser({ userId, username })

    if (isLoggedIn()) {
      this.props.fetchMyRoles()
    }

    hasWebPSupport(this.updateWebPSupport.bind(this))
  }

  updateWebPSupport(doesSupportWebP) {
    this.setState({ doesSupportWebP })
  }

  followEntity() {
    this.props.followEntity({ orgName: this.props.orgName })
  }

  unfollowEntity() {
    this.props.unfollowEntity({ orgName: this.props.orgName })
  }

  updateStory(values) {
    this.setState({ updatedStories: [].concat(...this.state.updatedStories, values) })
    this.props.updateStory(values)
  }

  renderOrgError() {
    return (
      <PageWrapper bgColor={'whitesmoke'} ogTitle={'Brownie Points'}>
        <div style={{ paddingTop: '100px' }}>
          <MissingOrgTitle>{c(l('ORG'))} not found</MissingOrgTitle>

          <OrgContainer>
            <MissingOrgText>
              You seem to have found yourself in the wrong place. Head back{' '}
              <ActionLink to={`/`}>home</ActionLink> and try again.
            </MissingOrgText>
          </OrgContainer>
        </div>
      </PageWrapper>
    )
  }

  renderLoadingMessage() {
    return (
      <PageWrapper bgColor={'whitesmoke'} ogTitle={'Brownie Points'}>
        <AnimatedLoader fullPage={false} message="Loading user profile" />
      </PageWrapper>
    )
  }

  render() {
    const { updatedStories } = this.state
    const {
      categories,
      hasError,
      followings,
      individual,
      handleStoryLike,
      isLoading,
      roleData
    } = this.props

    if (isLoading) {
      return this.renderLoadingMessage()
    }

    const displayNotFoundMessage = shouldDisplayPageNotFound({ location, individual })
    if (displayNotFoundMessage) {
      return (
        <PageWrapper bgColor={'whitesmoke'} ogTitle={'Brownie Points'}>
          <NotFoundWrapper>
            <MissingOrgTitle>Page not found</MissingOrgTitle>

            <MissingOrgText>
              You seem to be in the wrong place. Find your way back{' '}
              <ActionLink to={`/`}>home</ActionLink>.
            </MissingOrgText>
          </NotFoundWrapper>
        </PageWrapper>
      )
    }

    if (hasError) {
      return this.renderOrgError()
    }

    const isFollowing = followings.some(f => {
      return f.entity_id === individual.id && f.entity_type === 'User'
    })

    const individualName = `${individual.f_name} ${individual.l_name}`
    const ogTitle = `${'Brownie Points'} | ${individualName}`
    const loggedIn = isLoggedIn()
    const userData = getLocalStorageValues()

    const userCache = individual.userCache || {}
    const isSelf = loggedIn && `${individual.id}` === `${userData.id}`
    const hasGodRole = roleData.some(role => role.role_type === constants.ROLES.GOD_MODE)

    const { joinedChallenges, totalJoinedChallenges } = buildJoinedChallenges({ individual })
    const { hostedOpps, totalHostedOpps } = buildHostedOpps({ individual })
    const { joinedOpps, totalJoinedOpps } = buildJoinedOpps({ individual })
    const { supportedOrgs, totalSupportedOrgs } = buildSupportedOrgs({ individual })
    const { stories, totalStoryCount } = buildStories({ individual })
    const individualActivity = stories
      .map(s => {
        const isUpdated = updatedStories.map(us => us.id).includes(s.id)
        return { ...s, isHiddenStory: isUpdated }
      })
      .sort((b, a) => b.createdAt - a.createdAt)

    return (
      <PageWrapper
        ogDescription={individual.username}
        ogImage={individual.avatar_url}
        ogTitle={ogTitle}
        roleData={roleData}
        userData={userData}
      >
        <OrgContainer>
          <OrgIntroWrapper>
            <OrgIntroInnerWrapper>
              <OrgLogoContainer>
                <OrgLogoWrapper>
                  <OrgLogo
                    hasAvatar={individual.avatar_url}
                    src={individual.avatar_url || userIcon}
                  />
                </OrgLogoWrapper>
              </OrgLogoContainer>

              <OrgName>{individualName}</OrgName>

              <UserCountrySection individual={individual} />

              <OrgAdminActions>
                <ConditionalDisplay displayWhen={[!isSelf]}>
                  <SmallButtonPrimary
                    disabled={!loggedIn}
                    disabledHint={`Log in to follow.`}
                    display="block"
                    size={20}
                    icon={isFollowing ? UserUnfollow : UserFollow}
                    onClick={isFollowing ? this.unfollowEntity : this.followEntity}
                    warning={isFollowing}
                  >
                    {isFollowing ? `Unfollow` : `Follow`}
                  </SmallButtonPrimary>
                </ConditionalDisplay>

                <ConditionalDisplay displayWhen={[isSelf]}>
                  <ProfileLink
                    size={30}
                    icon={Pencil}
                    label={`Edit profile`}
                    linkUrl={`/account`}
                  />
                </ConditionalDisplay>
              </OrgAdminActions>
            </OrgIntroInnerWrapper>

            <UserSectionWrapperInner>
              <UserProfileStatsWrapper>
                <UserProfileStatsRow>
                  <UserProfileStatsImageWrapper>
                    <UserProfileStatsImage src={bpIcon} />
                  </UserProfileStatsImageWrapper>
                  <UserProfileStatValue>{userCache.points_count}</UserProfileStatValue>
                  <UserProfileStatLabel>brownie points</UserProfileStatLabel>
                </UserProfileStatsRow>

                {/* <UserProfileStatsRow>
                  <UserProfileStatsIcon>
                    <CalendarHeart />
                  </UserProfileStatsIcon>
                  <UserProfileStatValue>500</UserProfileStatValue>
                  <UserProfileStatLabel>best streak (in days)</UserProfileStatLabel>
                </UserProfileStatsRow> */}

                <UserProfileStatsRow>
                  <UserProfileStatsIcon>
                    <ChallengeIcon />
                  </UserProfileStatsIcon>
                  <UserProfileStatValue>{userCache.active_challenge_count}</UserProfileStatValue>
                  <UserProfileStatLabel>
                    {userCache.active_challenge_count === 1
                      ? 'challenge joined'
                      : 'challenges joined'}
                  </UserProfileStatLabel>
                </UserProfileStatsRow>

                <UserProfileStatsRow>
                  <UserProfileStatsIcon>
                    <StarFill />
                  </UserProfileStatsIcon>
                  <UserProfileStatValue>{userCache.achievement_count || 0}</UserProfileStatValue>
                  <UserProfileStatLabel>
                    {userCache.achievement_count === 1
                      ? 'achievement earned'
                      : 'achievements earned'}
                  </UserProfileStatLabel>
                </UserProfileStatsRow>

                <UserProfileStatsRow>
                  <UserProfileStatsIcon>
                    <OrgTypeIcon />
                  </UserProfileStatsIcon>
                  <UserProfileStatValue>{userCache.best_streak}</UserProfileStatValue>
                  <UserProfileStatLabel>
                    {userCache.best_streak === 1 ? 'day' : 'days'} best streak
                  </UserProfileStatLabel>
                </UserProfileStatsRow>

                <UserProfileStatsRow>
                  <UserProfileStatsIcon>
                    <OrgTypeIcon />
                  </UserProfileStatsIcon>
                  <UserProfileStatValue>{totalSupportedOrgs}</UserProfileStatValue>
                  <UserProfileStatLabel>nonprofits supported</UserProfileStatLabel>
                </UserProfileStatsRow>

                <UserProfileStatsRow>
                  <UserProfileStatsIcon>
                    <JournalText />
                  </UserProfileStatsIcon>
                  <UserProfileStatValue>{userCache.story_count}</UserProfileStatValue>
                  <UserProfileStatLabel>stories shared</UserProfileStatLabel>
                </UserProfileStatsRow>
              </UserProfileStatsWrapper>
            </UserSectionWrapperInner>
          </OrgIntroWrapper>

          <OrgProfileHorizontalSeparator />

          <UserSectionWrapper>
            <UserSectionWrapperInner>
              <OrgDescriptionLabel>Stories & Activity</OrgDescriptionLabel>

              <ConditionalDisplay displayWhen={[!stories.length]}>
                <OrgDetailLabel fallback={`${individual.f_name} does not have any activity yet.`} />
              </ConditionalDisplay>

              <StoryFeed
                categories={categories}
                columns={2}
                handleStoryLike={handleStoryLike}
                hasGodRole={hasGodRole}
                masonryClassName={'user-masonry'}
                stories={individualActivity}
                totalStoryCount={totalStoryCount}
                updateStory={values => this.updateStory(values)}
              />

              <ConditionalDisplay displayWhen={[totalStoryCount > 4]}>
                <ViewMoreLink to={`shared-stories`}>View all stories</ViewMoreLink>
              </ConditionalDisplay>
            </UserSectionWrapperInner>
          </UserSectionWrapper>

          <OrgProfileHorizontalSeparator />

          <UserSectionWrapper>
            <UserSectionWrapperInner>
              <OrgDescriptionLabel>Challenges</OrgDescriptionLabel>

              <ConditionalDisplay displayWhen={[!joinedChallenges.length]}>
                <OrgDetailLabel
                  fallback={`${individual.f_name} has not joined any challenges yet.`}
                />
              </ConditionalDisplay>

              <UserOppsBlock opps={joinedChallenges} />

              <ConditionalDisplay displayWhen={[totalJoinedChallenges > 4]}>
                <ViewMoreLink to={`joined-challenges`}>View more</ViewMoreLink>
              </ConditionalDisplay>
            </UserSectionWrapperInner>
          </UserSectionWrapper>

          <OrgProfileHorizontalSeparator />

          <UserSectionWrapper>
            <UserSectionWrapperInner>
              <OrgDescriptionLabel>Supported nonprofits</OrgDescriptionLabel>

              <UserSupportedOrgsBlock individual={individual} supportedOrgs={supportedOrgs} />

              <ConditionalDisplay displayWhen={[totalSupportedOrgs > 4]}>
                <ViewMoreLink to={`nonprofits`}>View more</ViewMoreLink>
              </ConditionalDisplay>
            </UserSectionWrapperInner>
          </UserSectionWrapper>

          <ConditionalDisplay displayWhen={[joinedOpps.length]}>
            <OrgProfileHorizontalSeparator />

            <UserSectionWrapper>
              <UserSectionWrapperInner>
                <OrgDescriptionLabel>Joined {l('OPPS')}</OrgDescriptionLabel>

                <UserOppsBlock opps={joinedOpps} />

                <ConditionalDisplay displayWhen={[totalJoinedOpps > 4]}>
                  <ViewMoreLink to={`joined`}>View more</ViewMoreLink>
                </ConditionalDisplay>
              </UserSectionWrapperInner>
            </UserSectionWrapper>
          </ConditionalDisplay>

          <ConditionalDisplay displayWhen={[hostedOpps.length]}>
            <OrgProfileHorizontalSeparator />

            <UserSectionWrapper>
              <UserSectionWrapperInner>
                <OrgDescriptionLabel>Hosted {l('OPPS')}</OrgDescriptionLabel>

                <UserOppsBlock opps={hostedOpps} />

                <ConditionalDisplay displayWhen={[totalHostedOpps > 4]}>
                  <ViewMoreLink to={`hosted`}>View more</ViewMoreLink>
                </ConditionalDisplay>
              </UserSectionWrapperInner>
            </UserSectionWrapper>
          </ConditionalDisplay>
        </OrgContainer>
      </PageWrapper>
    )
  }
}

const mapDispatchToProps = dispatch => ({
  fetchCategories: () => dispatch(categoriesModule.fetchCategories()),
  fetchMyRoles: () => dispatch(authModule.fetchMyRoles()),
  fetchPublicUser: q => dispatch(peopleModule.fetchPublicUser(q)),
  followEntity: q => dispatch(followingModule.followEntity(q)),
  handleStoryLike: q => dispatch(storiesModule.handleStoryLike(q)),
  unfollowEntity: q => dispatch(followingModule.unfollowEntity(q)),
  updateStory: q => dispatch(storiesModule.updateStory(q))
})

const mapStateToProps = state => {
  return {
    categories: state.categories.data || [],
    errorCode: state.people.errorCode,
    errorMessage: state.people.errorMessage,
    followings: state.authentication.followings || [],
    hasError: state.people.hasError,
    individual: state.people.singlePersonData || {},
    isLoading: state.people.isLoading,
    roleData: state.authentication.roleData || []
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(UserProfilePage)
